import { useState } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { User, Lock, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const UserProfile = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ email: '', current_password: '' });
  const [emailLoading, setEmailLoading] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      await axiosInstance.put('/users/me/email', emailForm);
      toast.success('E-mailadres gewijzigd — je logt vanaf nu in met het nieuwe adres');
      setEmailForm({ email: '', current_password: '' });
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error(t('passwordsDontMatch'));
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('current_password', passwordForm.current_password);
      formData.append('new_password', passwordForm.new_password);
      
      await axiosInstance.put('/users/change-password', formData);
      toast.success(t('passwordChangedSuccess'));
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-4xl font-bold text-gray-800">{t('userProfile')}</h1>

      {/* Informações do Usuário */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-500 text-sm">{t('name')}</Label>
            <p className="text-gray-800 text-lg font-medium">{user.name}</p>
          </div>
          <div>
            <Label className="text-gray-500 text-sm">{t('email')}</Label>
            <div className="flex items-center gap-2 text-gray-800">
              <Mail className="w-4 h-4 text-gray-400" />
              <p>{user.email}</p>
            </div>
          </div>
          {user.phone_number && (
            <div>
              <Label className="text-gray-500 text-sm">{t('phone')}</Label>
              <div className="flex items-center gap-2 text-gray-800">
                <Phone className="w-4 h-4 text-gray-400" />
                <p>{user.phone_number}</p>
              </div>
            </div>
          )}
          {user.hourly_rate > 0 && (
            <div>
              <Label className="text-gray-500 text-sm">Hourly Rate</Label>
              <p className="text-gray-800 text-lg font-medium">€{user.hourly_rate}/hour</p>
            </div>
          )}
          <div>
            <Label className="text-gray-500 text-sm">Account Type</Label>
            <span data-testid="profile-role-badge" className={`inline-block px-3 py-1 rounded text-sm ${
              user.role === 'esquarcio' ? 'bg-[#0f3b57] text-white' : user.role === 'boekhouder' ? 'bg-[#1a5276]/15 text-[#1a5276]' : user.is_admin ? 'bg-purple-600/20 text-purple-400' : 'bg-blue-600/20 text-blue-400'
            }`}>
              {user.role === 'esquarcio' ? '🧽 Esquarcio Schoonmaakbedrijf' : user.role === 'boekhouder' ? '📊 Boekhouder (alleen lezen)' : user.is_admin ? `👑 ${t('admin')}` : `👷 ${t('employee')}`}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* E-mail wijzigen */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            E-mailadres wijzigen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <div>
              <Label className="text-gray-800 mb-2 block">Nieuw e-mailadres</Label>
              <Input type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} required placeholder="nieuw@adres.nl" className="bg-gray-50 border-white/30 text-gray-800" data-testid="profile-new-email" />
            </div>
            <div>
              <Label className="text-gray-800 mb-2 block">Huidig wachtwoord (ter bevestiging)</Label>
              <Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} required className="bg-gray-50 border-white/30 text-gray-800" data-testid="profile-email-password" />
            </div>
            <Button type="submit" disabled={emailLoading} className="w-full bg-[#1a5276] hover:bg-[#154360] font-semibold py-6" data-testid="profile-email-submit">
              {emailLoading ? t('loading') : 'E-mailadres opslaan'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Alterar Senha */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {t('changePassword')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-gray-800 mb-2 block">{t('currentPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                required
                className="bg-gray-50 border-white/30 text-gray-800"
                placeholder={t('currentPassword')}
              />
            </div>
            <div>
              <Label className="text-gray-800 mb-2 block">{t('newPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                required
                minLength={6}
                className="bg-gray-50 border-white/30 text-gray-800"
                placeholder={t('newPassword')}
              />
            </div>
            <div>
              <Label className="text-gray-800 mb-2 block">{t('confirmNewPassword')}</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                required
                minLength={6}
                className="bg-gray-50 border-white/30 text-gray-800"
                placeholder={t('confirmNewPassword')}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] font-semibold py-6"
              data-testid="profile-password-submit"
            >
              {loading ? `${t('loading')}` : t('changePassword')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Botão Sair */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardContent className="pt-6">
          <Button
            onClick={onLogout}
            variant="outline"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            {t('logout')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserProfile;